import { NoData } from "@/components/common/section-components/NoData";
import { SamplingRequestActions } from "@/components/common/section-components/action/SamplingRequestActions";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { SamplingRequestFilter } from "@/components/common/section-components/filters/SamplingRequestFilter";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { ServiceRequestStatus } from "@/constants/entity-status";
import { UnidadCotizacionServicio } from "@/constants/enums";
import { AppRoutes } from "@/constants/routes";
import dayjs from "dayjs";
import { Flex } from "@chakra-ui/react";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  clearSamplingFiltersHandler,
  fetchSamplingRequestsHandler,
  submitSamplingFiltersHandler,
} from "./schema/handler-functions";
import {
  SamplingRequestListItem,
  SamplingRequestsListResponse,
} from "./schema/sampling-requests.schema";
import { samplingRequestsHeaderElements } from "./schema/header-elements";
import { useRoles } from "@/hooks/useRoles";
import { PermissionCode } from "@/constants/permissions";
import { EstadosSolicitudServicio } from "@/constants/enums/status/entity-status-ids";

export const SamplingRequestsPage = () => {
  const navigate = useNavigate();
  const { fetchPermissions, hasWriteAccess } = useRoles();

  const handleAdd = () => {
    navigate(AppRoutes.SAMPLING_REQUEST_CREATION);
  };

  const [isActive, setIsActive] = useState<boolean>(false);
  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [list, setList] = useState<SamplingRequestsListResponse>();
  const [currentFilters, setCurrentFilters] = useState<any>();
  const [page, setPage] = useState<number>(1);
  const defaultStatusFilters = [EstadosSolicitudServicio.Pendiente];

  const { control, handleSubmit, setValue, getValues } = useForm<FieldValues>({
    defaultValues: {
      estados: defaultStatusFilters,
    },
  });
  const values = useWatch({ control });
  const filtersEmpty = useMemo(
    () => Object.values(values ?? {}).every((v) => isEmpty(v)),
    [values],
  );

  useEffect(() => {
    setIsActive(true);
  }, []);

  const fetchList = async (filters?: any, showLoad = true) => {
    await fetchSamplingRequestsHandler({
      filters,
      showLoad,
      setLoading,
      setList,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (isActive) {
      setValue("estados", defaultStatusFilters);
      submitSamplingFiltersHandler({
        event: getValues(),
        setPage,
        fetchList,
        setCurrentFilters,
        setFiltersApplied,
        setOpenFilters,
        filtersEmpty: false,
      });
    }
  }, [getValues, isActive, setValue]);

  const submitFilters = async (event: FieldValues) => {
    await submitSamplingFiltersHandler({
      event,
      setPage,
      fetchList,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      filtersEmpty,
    });
  };

  const clearFilters = async () => {
    await clearSamplingFiltersHandler({
      setValue,
      setFiltersApplied,
      setOpenFilters,
      setPage,
      setCurrentFilters,
      fetchList,
    });
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchList({ ...currentFilters, "P.Pagina": nextPage });
  };

  const handleDetail = (id: number) => {
    navigate(AppRoutes.SAMPLING_REQUEST_DETAIL.replace(":id", String(id)));
  };

  const getActionElements = (item: SamplingRequestListItem) => (
    <SamplingRequestActions handleDetail={handleDetail} item={item} />
  );

  const getDisplayCantidad = (el: SamplingRequestListItem) => {
    if (Number(el?.unidadCotizacion) === UnidadCotizacionServicio.CantidadMuestra) {
      return `${new Intl.NumberFormat("es-AR").format(el?.cantMuestras ?? 0)} muestras`;
    }
    return `${new Intl.NumberFormat("es-AR").format(el?.hectareas ?? 0)} ha`;
  };

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Solicitud de muestreo"
        addButtonLabel="Agregar"
        addButtonType="button"
        handleAdd={handleAdd}
        loading={loading}
        showButtons={hasWriteAccess(PermissionCode.SAMPLING_REQUESTS)}
      />

      <SamplingRequestFilter
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        control={control}
        handleSubmit={handleSubmit}
        filtersApplied={filtersApplied}
        disableFilters={loading}
        loadingFilters={loading}
        disableApply={filtersEmpty && !filtersApplied}
      />

      <Flex flexDir="column">
        {Boolean(!loading && !list?.listado?.length) && <NoData />}

        {Boolean(!loading && list?.listado?.length) && (
          <>
            <StandardTable
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection
              items={
                list?.listado?.map((el: SamplingRequestListItem) => ({
                  ...el,
                  fecha: el?.fecha ? dayjs(el.fecha).format("DD/MM/YYYY") : "-",
                  displayCantidad: getDisplayCantidad(el),
                  displayEstado: (
                    <RequestStatusBadge
                      status={el?.estado as unknown as ServiceRequestStatus}
                    />
                  ),
                  procedencia: `${el?.procedencia} - ${el?.hectareas ?? 0} ha`,
                })) ?? []
              }
              headers={samplingRequestsHeaderElements}
              actionElements={getActionElements}
            />
            {Number(list?.totalPages) > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={list?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};